import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getEditSoftwareData, updateSoftwareAsset } from '../../../../models/assetsModel';
import Modal from '../../../../components/common/Modal';

function EditSoftwareModal({ isOpen, onClose, onSuccess, assetId }) {
  const [formData, setFormData] = useState({
    asset_id: '',
    asset_name: '',
    asset_description: '',
    asset_category_id: '',
    asset_vendor_id: '',
    asset_cost: '',
    asset_license_key: '',
    asset_expiry: '', // Changed from asset_license_expiry
    asset_users: '', // Changed from asset_max_users
    asset_date_purchased: '' // Added to match original form
  });
  const [formOptions, setFormOptions] = useState({
    categories: [],
    vendors: []
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && assetId) {
      fetchEditData();
    }
  }, [isOpen, assetId]);

  const fetchEditData = async () => {
    try {
      setLoading(true);
      const data = await getEditSoftwareData(assetId);
      
      if (data) {
        // Set form data - map backend fields to form fields
        setFormData({
          asset_id: assetId,
          asset_name: data.asset_name || '',
          asset_description: data.asset_description || '',
          asset_category_id: data.asset_category_id || '',
          asset_vendor_id: data.asset_vendor_id || '',
          asset_cost: data.asset_cost || '',
          asset_license_key: data.asset_license_key || '',
          asset_expiry: data.asset_expiry_date || '', // Backend uses asset_expiry_date
          asset_users: data.asset_users || '', // Backend uses asset_users
          asset_date_purchased: data.asset_date_purchased || ''
        });
        
        // Set form options
        setFormOptions({
          categories: data.categories || [],
          vendors: data.vendors || []
        });
      }
    } catch (error) {
      console.error('Error fetching edit data:', error);
      toast.error('Failed to load software data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.asset_name.trim()) {
      toast.error('Software name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Log the data being sent for debugging
      console.log('Updating software data:', formData);
      
      const result = await updateSoftwareAsset(formData);
      
      if (result) {
        toast.success('Software asset updated successfully!');
        onSuccess();
      }
    } catch (error) {
      console.error('Error updating software asset:', error);
      toast.error('Failed to update software asset');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      asset_id: '',
      asset_name: '',
      asset_description: '',
      asset_category_id: '',
      asset_vendor_id: '',
      asset_cost: '',
      asset_license_key: '',
      asset_expiry: '',
      asset_users: '',
      asset_date_purchased: ''
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Edit Software Asset" size="lg">
        <div className="flex flex-col justify-center items-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 text-sm">Loading software data...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Software Asset" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Software Details Section */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center mb-3">
            <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900">Software Details</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                <span className="flex items-center">
                  <span className="text-red-500 mr-1">*</span>
                  Software Name
                </span>
              </label>
              <input
                type="text"
                name="asset_name"
                value={formData.asset_name}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter software name"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Category
              </label>
              <select
                name="asset_category_id"
                value={formData.asset_category_id}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Category</option>
                {formOptions.categories.map((category) => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Vendor
              </label>
              <select
                name="asset_vendor_id"
                value={formData.asset_vendor_id}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Vendor</option>
                {formOptions.vendors.map((vendor) => (
                  <option key={vendor.category_id} value={vendor.category_id}>
                    {vendor.category_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Date Purchased
              </label>
              <input
                type="date"
                name="asset_date_purchased"
                value={formData.asset_date_purchased}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="asset_description"
              value={formData.asset_description}
              onChange={handleInputChange}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Enter software description"
            />
          </div>
        </div>

        {/* License Information Section */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center mb-3">
            <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900">License & Pricing</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Cost
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-xs font-medium">R</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  name="asset_cost"
                  value={formData.asset_cost}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Number of Users
              </label>
              <input
                type="number"
                name="asset_users"
                value={formData.asset_users}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                placeholder="Unlimited"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                License Key
              </label>
              <input
                type="text"
                name="asset_license_key"
                value={formData.asset_license_key}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors font-mono"
                placeholder="Enter license key or serial number"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Expiry Date
              </label>
              <input
                type="date"
                name="asset_expiry"
                value={formData.asset_expiry}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 transition-colors shadow-sm"
          >
            <svg className="w-3 h-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white inline" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : (
              <>
                <svg className="w-3 h-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Update Software
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default EditSoftwareModal;
